import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import type { ComponentType } from "react";

export interface WorkflowVariableTokenAttributes {
  token: string;
  label: string;
  fallback: string;
}

export interface WorkflowVariableTokenOptions {
  HTMLAttributes: Record<string, unknown>;
  component?: ComponentType<NodeViewProps>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    workflowVariableToken: {
      setWorkflowVariableToken: (
        attributes: WorkflowVariableTokenAttributes,
      ) => ReturnType;
    };
  }
}

export const WorkflowVariableToken = Node.create<WorkflowVariableTokenOptions>({
  name: "workflowVariableToken",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: "inline",

  inline: true,

  selectable: true,

  atom: true,

  addAttributes() {
    return {
      token: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-token"),
        renderHTML: (attributes) =>
          attributes.token ? { "data-token": attributes.token } : {},
      },
      label: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-label") || "",
        renderHTML: (attributes) =>
          attributes.label ? { "data-label": attributes.label } : {},
      },
      fallback: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-fallback") || "",
        renderHTML: (attributes) => ({
          "data-fallback": attributes.fallback || "",
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-token]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        {
          "data-type": this.name,
          class: "workflow-variable-token",
        },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      node.attrs.token,
    ];
  },

  renderText({ node }) {
    return node.attrs.token;
  },

  addNodeView() {
    if (this.options.component) {
      return ReactNodeViewRenderer(this.options.component);
    }
    return null;
  },

  addCommands() {
    return {
      setWorkflowVariableToken:
        (attributes) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: attributes,
          }),
    };
  },

  addKeyboardShortcuts() {
    const removeAdjacentToken =
      (direction: "backward" | "forward") =>
      () =>
        this.editor.commands.command(({ tr, state }) => {
          const { selection } = state;
          if (!selection.empty) return false;

          const position =
            direction === "backward" ? selection.anchor - 1 : selection.anchor;
          if (position < 0) return false;

          let removed = false;
          state.doc.nodesBetween(position, position + 1, (node, pos) => {
            if (node.type.name !== this.name) return;
            tr.delete(pos, pos + node.nodeSize);
            removed = true;
            return false;
          });

          return removed;
        });

    return {
      Backspace: removeAdjacentToken("backward"),
      Delete: removeAdjacentToken("forward"),
    };
  },
});

export default WorkflowVariableToken;
